import _ from 'lodash';
import Builder from 'react-native-jala-orm/src/model/Builder';
import Expression from 'react-native-jala-orm/src/query/Expression';
import { QueryBuilder } from 'react-native-jala-orm/src/query/Builder';
import SyncableModel from './SyncableModel';
import moment from "moment";
import { translate } from "../i18n"

export default class Region extends SyncableModel {
    static name = 'Region'

    _fillable = [
      'id',
      'name',
      'type',
      'latitude',
      'longitude',
      'country_id',
    ]

    incrementing = false

    _keyType = 'string'

    timestamps = false

    static syncOnly = true

    static hasSyncedAt = false

    static endpoint = '/regions'

    static searchables = ['name', 'full_name']

    _casts = {
      latitude: 'string',
      longitude: 'string',
    }

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) query.select([query.qualifyColumn('*')]);
      });

      this.addGlobalScope('full_name', (query: Builder) => {
        ['provinces', 'regencies', 'districts'].forEach((table) => {
          const region: Region = (new Region()).setTable(`${query.getModel().getTable()}_${table}`);
          query.leftJoin(
            `${(new Region()).getTable()} as ${region.getTable()}`,
            (q: QueryBuilder) => {
              const length = (table === 'villages' && 10) || (table === 'districts' && 7) || (table === 'regencies' && 4) || 2;
              return q.whereRaw(`LENGTH(${region.qualifyColumn('id')}) = ${length}`)
                .whereColumn(region.getQualifiedKeyName(), new Expression(`SUBSTR(${query.getModel().getQualifiedKeyName()}, 1, ${length})`));
            }
          );
        });
        if (!_.isEmpty(query.getQuery().columns) && query.getQuery().columns.length === 1) {
          if (_.head(query.getQuery().columns) === query.qualifyColumn('*')) {
            query.addSelect(new Expression(`${query.qualifyColumn('full_name')} as full_name`));
          }
        }
      });
    }

    /**
     * Qualify the given column name by the model's table.
     *
     * @return string
     * @param {String} column
     */
    qualifyColumn(column) {
      if (column === 'full_name') {
        return new Expression(`(${
          ['provinces', 'regencies', 'districts'].map((table, index) => {
            const region = (new Region()).setTable(`${this.getTable()}_${table}`);
            return `(CASE WHEN ${region.getQualifiedKeyName()} IS NOT NULL THEN (${(index === 0 && "''") || "', '"} || ${region.qualifyColumn('name')}) ELSE '' END)`;
          }).join(' || ')
        })`);
      }
      if (column === 'updated_at') {
        return new Expression(`'${moment().utc().format('YYYY-MM-DD HH:mm:ss')}'`);
      }
      if (column === 'synced_at') {
        return new Expression('\'2001-01-01 00:00:00\'');
      }
      if (column === 'deleted_at') {
        return new Expression('NULL');
      }
      if (column === 'country_id') {
        return new Expression(`COALESCE(${this.getTable()}.country_id, 'ID')`);
      }
      if ([ 'province_', 'regency_', 'district_', 'village_' ].find((k) => column.startsWith(k))) {
        const region = column.split('_')
        const table = region[0] === 'province' && 'provinces'
          || region[0] === 'regency' && 'regencies'
          || region[0] === 'district' && 'districts'
          || region[0] === 'village' && 'villages'
        return `${this.getTable()}_${table}.${region[1]}`
      }

      return super.qualifyColumn(column);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeToProvince(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) = 2`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeToRegency(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) = 4`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeToDistrict(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) = 7`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeToVillage(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) = 10`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeByProvince(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) <= 2`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeByRegency(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) <= 4`);
    }

    /**
     * @param {Builder|QueryBuilder} query
     * @returns {Builder|QueryBuilder}
     */
    scopeByDistrict(query) {
      return query.whereRaw(`LENGTH(${query.qualifyColumn('id')}) <= 7`);
    }

    setSyncedAtAttribute(value) {
      return this;
    }

    getNameAttribute(value) {
      if (this.getKey() === '99')
        return translate('regions.outside_indonesia')
      return value
    }

    getFullNameAttribute(value) {
      if (this.getKey() === '99')
        return translate('regions.outside_indonesia')
      return value
    }

    static applyFetchParamsToQuery(query, params, param) {
      super.applyFetchParamsToQuery(query, params, param);
      if (param === 'scope_only' && ['province', 'regency', 'district', 'village'].includes(params[param])) {
        query[_.camelCase(`to_${params[param]}`)] && query[_.camelCase(`to_${params[param]}`)]();
      }
      if (param === 'scope' && ['province', 'regency', 'district'].includes(params[param])) {
        query[_.camelCase(`by_${params[param]}`)] && query[_.camelCase(`by_${params[param]}`)]();
      }
      return query;
    }

    static async syncAll(
      params = {}, sqlParams = [], cleanAfterSync = true,
      onSyncProgressUpdate = undefined, onError = undefined
    ) {
      const _params = { ...params };
      const _sqlParams = [...sqlParams];
      if (_.isEmpty(params) && !sqlParams.length) {
        await super.syncAll({ has: 'farms.farm_user' }, [], false)
        const countries = ['EC', 'ES', 'VN'];
        _params.country_id__in = countries.join(',');
        _sqlParams.push(['regions.country_id', countries]);
      }
      return super.syncAll(_params, _sqlParams, cleanAfterSync, onSyncProgressUpdate, onError);
    }

    static reformatResponse(response, raw = false) {
      const attributes = {
        ...(new this()).fill(response)[raw ? 'getAttributes' : 'toArray'](),
      }
      // Trim trailing zeros string
      attributes.latitude = attributes.latitude && attributes.latitude.replace(/\.?0+$/, '');
      attributes.longitude = attributes.longitude && attributes.longitude.replace(/\.?0+$/, '');
      return _.omit(attributes, ['synced_at', 'deleted_at']);
    }

}
